import { Mark } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { ReplaceStep } from "@tiptap/pm/transform";
import { TextSelection } from "prosemirror-state";
import { TRACKCHANGE, USERROLE } from "../../../utils/commonConstant";
import { Hidden } from "@mui/material";
import toast from "react-hot-toast";

// **Define a consistent meta key to skip track changes processing**
const SKIP_TRACK_CHANGES_META = "skipTrackChanges";
let changesTextAddedDeleted = [];
// Singleton popup management
let activeChangePopup = null;

let trackChangePersonName = USERROLE.ADMIN;
// Function to get user ID from localStorage
const getUserIdFromStorage = () => {
  return localStorage.getItem("UserId") || "unknown";
};

// Function to get user name from localStorage
const getUserNameFromStorage = () => {
  return localStorage.getItem("Name") || "Anonymous";
};
// Function to get user name from localStorage
const getUserRoleFromStorage = () => {
  return localStorage.getItem("Role") || "Client";
};

// Helper function to create comment UI with threading
function createChangeUI(
  change,
  view,
  storage,
  left = null,
  top = null,
  selectedCurrentMark = null,
  selectedNextMark = null,
  handleSave = null,
  trackHistorys,
  extension
) {
  // If an existing popup is open, remove it first
  if (activeChangePopup) {
    activeChangePopup.remove();
  }
  console.log(selectedNextMark, "nextMark");
  console.log(selectedCurrentMark, "currentMark");
  const container = document.createElement("div");
  container.className = "track-changes-container";
  container.id = "track-changes-div"

  // Close button
  const closeButton = document.createElement("button");
  closeButton.className = "close-button";
  closeButton.textContent = "✕";
  closeButton.onclick = () => {
    container.remove();
    activeChangePopup = null;
    console.log("Popup closed");
  };

  // User info
  const userInfo = document.createElement("div");
  userInfo.className = "change-user-info";

  const formattedDate = change.timestamp
    ? new Date(Number(change.timestamp)).toLocaleString()
    : "Unknown date";

  userInfo.innerHTML = `
    <strong>User:</strong> ${change.userName || "Anonymous"}<br>
    <strong>Date:</strong> ${formattedDate}
  `;

  // Change details
  const changeDetails = document.createElement("div");
  changeDetails.className = "change-details";

  let changeText = "";
  if (selectedNextMark && selectedCurrentMark) {
    if (
      selectedCurrentMark.mark.attrs.type === "added" &&
      selectedNextMark.mark.attrs.type === "deleted"
    ) {
      changeText = `Replaced "<strong>${selectedNextMark.mark.attrs.originalText}</strong>" with "<strong>${selectedCurrentMark.mark.attrs.originalText}</strong>"`;
    } else if (
      selectedCurrentMark.mark.attrs.type === "deleted" &&
      selectedNextMark.mark.attrs.type === "added"
    ) {
      changeText = `Replaced "<strong>${selectedCurrentMark.mark.attrs.originalText}</strong>" with "<strong>${selectedNextMark.mark.attrs.originalText}</strong>"`;
    }
    //handel track change and  comment  for same text
    else if (
      selectedCurrentMark.mark.attrs.type === "added" &&
      selectedNextMark.mark.attrs.type === "added"
    ) {
      changeText = `Added "<strong>${selectedCurrentMark.mark.attrs.originalText}</strong>"`;
    } else if (
      selectedCurrentMark.mark.attrs.type === "deleted" &&
      selectedNextMark.mark.attrs.type === "deleted"
    ) {
      changeText = `Deleted "<strong>${selectedCurrentMark.mark.attrs.originalText}</strong>"`;
    }
  } else {
    if (change.type === "added") {
      changeText = `Added "<strong>${change.originalText}</strong>"`;
    } else if (change.type === "deleted") {
      changeText = `Deleted "<strong>${change.fullText}</strong>"`;
    } else if (change.type === "modified") {
      changeText = `Replaced "<strong>${change.originalText}</strong>" with "<strong>${change.newText}</strong>"`;
    }
  }

  changeDetails.innerHTML = `
    <strong>Change:</strong> ${changeText}
  `;
  // Visibility section
  const visibilitySection = document.createElement("div");
  visibilitySection.className = "visibility-section";

  const visibilityTitle = document.createElement("h4");
  visibilityTitle.textContent = "Visibility";
  visibilitySection.appendChild(visibilityTitle);

  const clientCheckbox = document.createElement("input");
  clientCheckbox.type = "checkbox";
  clientCheckbox.id = "visibility-client";
  clientCheckbox.value = "Client";

  const clientLabel = document.createElement("label");
  clientLabel.htmlFor = "visibility-client";
  clientLabel.textContent = "Client";

  const markelCheckbox = document.createElement("input");
  markelCheckbox.type = "checkbox";
  markelCheckbox.id = "visibility-markel";
  markelCheckbox.value = "Markel";

  const markelLabel = document.createElement("label");
  markelLabel.htmlFor = "visibility-markel";
  markelLabel.textContent = "Markel";
  // Set the checkboxes based on the VisibilityFor attribute
  if (change.VisibilityFor) {
    let visibilityFor;
    // Convert VisibilityFor to an array if it's not already
    if (typeof change.VisibilityFor === "string") {
      visibilityFor = change.VisibilityFor.split(",").map((item) =>
        item.trim()
      );
    } else if (Array.isArray(change.VisibilityFor)) {
      visibilityFor = change.VisibilityFor;
    }

    clientCheckbox.checked = visibilityFor.includes("Client");
    markelCheckbox.checked = visibilityFor.includes("Markel");
  } else {
    // If VisibilityFor has no value, set "Client" as the default
    clientCheckbox.checked = false;
    markelCheckbox.checked = false;
  }

  visibilitySection.appendChild(clientCheckbox);
  visibilitySection.appendChild(clientLabel);
  visibilitySection.appendChild(markelCheckbox);
  visibilitySection.appendChild(markelLabel);

  // Save visibility to storage
  const saveVisibility = () => {
    const visibilityFor = [];
    if (clientCheckbox.checked) visibilityFor.push("Client");
    if (markelCheckbox.checked) visibilityFor.push("Markel");
    if (visibilityFor.length !== 0) {
      // Update the mark's attribute dynamically
      const { state, dispatch } = view;
      const tr = state.tr;
      // Find the mark in the document and update its attributes
      state.doc.nodesBetween(change.from, change.to, (node, pos) => {
        const mark = node.marks.find((m) => m.type.name === "trackChanges");
        if (mark) {
          const updatedMark = state.schema.marks.trackChanges.create({
            ...mark.attrs,
            VisibilityFor: visibilityFor, // Update the VisibilityFor attribute
          });
          tr.removeMark(
            pos,
            pos + node.nodeSize,
            state.schema.marks.trackChanges
          );
          tr.addMark(pos, pos + node.nodeSize, updatedMark);
        }
      });
      //this condition is for modification selectedCurrentMark is change which is handle in handleClick fun so  selectedCurrentMark  attrs is not updated
      if (selectedCurrentMark && selectedNextMark) {
        // Find the mark in the document and update its attributes
        state.doc.nodesBetween(
          selectedNextMark.leftPos,
          selectedNextMark.rightPos,
          (node, pos) => {
            const mark = node.marks.find((m) => m.type.name === "trackChanges");
            if (mark) {
              const updatedMark = state.schema.marks.trackChanges.create({
                ...mark.attrs,
                VisibilityFor: visibilityFor, // Update the VisibilityFor attribute
              });
              tr.removeMark(
                pos,
                pos + node.nodeSize,
                state.schema.marks.trackChanges
              );
              tr.addMark(pos, pos + node.nodeSize, updatedMark);
            }
          }
        );
      }

      dispatch(tr);

      console.log("Visibility updated:", visibilityFor);
    }
  };

  clientCheckbox.addEventListener("change", saveVisibility);
  markelCheckbox.addEventListener("change", saveVisibility);

  // Comments section
  const commentsSection = document.createElement("div");
  commentsSection.className = "comments-section";

  const commentsTitle = document.createElement("h4");
  commentsTitle.textContent = "Comments";
  commentsSection.appendChild(commentsTitle);

  const commentsList = document.createElement("div");
  commentsList.className = "comments-list";

  const existingComments = (storage && storage[change.id]) || [];
  if (existingComments.length === 0) {
    commentsList.innerHTML = "<p>No comments yet.</p>";
  } else {
    const currentUserRole = getUserRoleFromStorage();

    existingComments
      .filter((comment) => {
        return (
          currentUserRole === USERROLE.ADMIN ||
          !comment.visibilityFor ||
          comment.visibilityFor.includes(currentUserRole)
        );
      })
      .forEach((comment) => {
        const commentDiv = document.createElement("div");
        commentDiv.className = "comment-thread";

        const commentHeader = document.createElement("div");
        commentHeader.className = "comment-header";

        const commentUser = document.createElement("strong");
        commentUser.textContent = comment.userName;

        // Add visibility checkboxes in the middle of the header
        const commentVisibility = document.createElement("span");
        commentVisibility.className = "comment-visibility-info";

        // Create Client checkbox
        const clientCheckbox = document.createElement("input");
        clientCheckbox.type = "checkbox";
        clientCheckbox.id = `comment-visibility-client-${comment.id}`;
        clientCheckbox.value = "Client";
        clientCheckbox.disabled =
          extension.options.isTrackingHistoryEnabled ||
          getUserRoleFromStorage() !== USERROLE.ADMIN;
        clientCheckbox.checked =
          comment.visibilityFor?.includes("Client") || false;

        const clientLabel = document.createElement("label");
        clientLabel.htmlFor = `comment-visibility-client-${comment.id}`;
        clientLabel.textContent = "Client";

        // Create Markel checkbox
        const markelCheckbox = document.createElement("input");
        markelCheckbox.type = "checkbox";
        markelCheckbox.id = `comment-visibility-markel-${comment.id}`;
        markelCheckbox.value = "Markel";
        markelCheckbox.disabled =
          extension.options.isTrackingHistoryEnabled ||
          getUserRoleFromStorage() !== USERROLE.ADMIN;
        markelCheckbox.checked =
          comment.visibilityFor?.includes("Markel") || false;

        const markelLabel = document.createElement("label");
        markelLabel.htmlFor = `comment-visibility-markel-${comment.id}`;
        markelLabel.textContent = "Markel";

        // Append checkboxes and labels to the span
        commentVisibility.appendChild(clientCheckbox);
        commentVisibility.appendChild(clientLabel);
        commentVisibility.appendChild(markelCheckbox);
        commentVisibility.appendChild(markelLabel);

        // Add event listeners to update visibilityFor dynamically
        clientCheckbox.addEventListener("change", () => {
          updateCommentVisibility(comment, clientCheckbox, markelCheckbox);
        });
        markelCheckbox.addEventListener("change", () => {
          updateCommentVisibility(comment, clientCheckbox, markelCheckbox);
        });

        // Function to update visibilityFor dynamically
        function updateCommentVisibility(
          comment,
          clientCheckbox,
          markelCheckbox
        ) {
          const visibilityFor = [];
          if (clientCheckbox.checked) visibilityFor.push("Client");
          if (markelCheckbox.checked) visibilityFor.push("Markel");
          comment.visibilityFor = visibilityFor;
          // Update the storage if it exists
          if (storage && storage[comment.id]) {
            const commentIndex = storage[comment.id].findIndex(
              (storedComment) => storedComment.id === comment.id
            );
            if (commentIndex !== -1) {
              storage[comment.id][commentIndex].visibilityFor = visibilityFor;
            }
          }

          console.log("Updated visibilityFor:", visibilityFor);
        }
        commentVisibility.style.margin = "0 8px"; // Add spacing around the
        const commentDate = document.createElement("span");
        commentDate.className = "comment-date";
        commentDate.textContent = ` (${new Date(
          comment.timestamp
        ).toLocaleString()})`;

        commentHeader.appendChild(commentUser);
        if (getUserRoleFromStorage() === USERROLE.ADMIN) {
          commentHeader.appendChild(commentVisibility); // Append visibility info
        }
        commentHeader.appendChild(commentDate);

        const commentText = document.createElement("p");
        commentText.className = "comment-text";
        commentText.textContent = comment.text;

        commentDiv.appendChild(commentHeader);
        commentDiv.appendChild(commentText);

        commentsList.appendChild(commentDiv);
      });
  }

  commentsSection.appendChild(commentsList);

  // Add comment form
  const addCommentForm = document.createElement("form");
  addCommentForm.className = "add-comment-form";
  // if (
  //   trackChangePersonName === USERROLE.ADMIN ||
  //   trackChangePersonName === USERROLE.MARKEL
  // ) {
  // Visibility checkboxes for the comment
  const commentVisibilitySection = document.createElement("div");
  commentVisibilitySection.className = "comment-visibility-section";

  const commentVisibilityTitle = document.createElement("h4");
  commentVisibilityTitle.textContent = "Comment Visibility";
  commentVisibilitySection.appendChild(commentVisibilityTitle);

  const commentClientCheckbox = document.createElement("input");
  commentClientCheckbox.type = "checkbox";
  commentClientCheckbox.id = "comment-visibility-client";
  commentClientCheckbox.value = "Client";
  commentClientCheckbox.checked = true; // Default to checked

  const commentClientLabel = document.createElement("label");
  commentClientLabel.htmlFor = "comment-visibility-client";
  commentClientLabel.textContent = "Client";

  const commentMarkelCheckbox = document.createElement("input");
  commentMarkelCheckbox.type = "checkbox";
  commentMarkelCheckbox.id = "comment-visibility-markel";
  commentMarkelCheckbox.value = "Markel";

  const commentMarkelLabel = document.createElement("label");
  commentMarkelLabel.htmlFor = "comment-visibility-markel";
  commentMarkelLabel.textContent = "Markel";

  commentVisibilitySection.appendChild(commentClientCheckbox);
  commentVisibilitySection.appendChild(commentClientLabel);
  commentVisibilitySection.appendChild(commentMarkelCheckbox);
  commentVisibilitySection.appendChild(commentMarkelLabel);

  // Append the visibility section to the form
  if (getUserRoleFromStorage() === USERROLE.ADMIN) {
    addCommentForm.appendChild(commentVisibilitySection);
  }
  const commentInput = document.createElement("textarea");
  commentInput.placeholder = "Add a comment...";
  commentInput.required = true;
  commentInput.rows = 3;

  const submitButton = document.createElement("button");
  submitButton.type = "submit";
  submitButton.textContent = "Add Comment";
  submitButton.className = "add-comment-button";

  addCommentForm.appendChild(commentInput);
  addCommentForm.appendChild(submitButton);

  addCommentForm.onsubmit = (e) => {
    e.preventDefault();
    const commentText = commentInput.value.trim();
    if (commentText === "") return;
    const visibilityFor = [];
    const userRole = getUserRoleFromStorage();

    const visibility =
      userRole === USERROLE.ADMIN
        ? (commentClientCheckbox.checked && visibilityFor.push("Client"),
          commentMarkelCheckbox.checked && visibilityFor.push("Markel"))
        : visibilityFor.push(
            userRole === USERROLE.MARKEL ? "Markel" : "Client"
          );

    const newComment = {
      id: `thread-${Date.now()}`,
      userName: getUserNameFromStorage(),
      userRole: getUserRoleFromStorage(),
      timestamp: Date.now(),
      text: commentText,
      status: "open",
      visibilityFor: visibilityFor.length > 0 ? visibilityFor : ["Client"], // Default to "Client" if none selected
    };

    // Add comment to storage
    if (!storage[change.id]) {
      storage[change.id] = [];
    }

    storage[change.id].push(newComment);

    // Update UI
    const commentDiv = document.createElement("div");
    commentDiv.className = "comment-thread";

    const commentHeader = document.createElement("div");
    commentHeader.className = "comment-header";

    const commentUser = document.createElement("strong");
    commentUser.textContent = newComment.userName;

    const commentDate = document.createElement("span");
    commentDate.className = "comment-date";
    commentDate.textContent = ` (${new Date(
      newComment.timestamp
    ).toLocaleString()})`;

    commentHeader.appendChild(commentUser);
    commentHeader.appendChild(commentDate);

    const commentTextElement = document.createElement("p");
    commentTextElement.className = "comment-text";
    commentTextElement.textContent = newComment.text;

    commentDiv.appendChild(commentHeader);
    commentDiv.appendChild(commentTextElement);

    if (
      commentsList.querySelector("p") &&
      commentsList.querySelector("p").textContent === "No comments yet."
    ) {
      commentsList.innerHTML = "";
    }
    commentsList.appendChild(commentDiv);

    // Capture current popup's position before removing
    const rect = container.getBoundingClientRect();

    // Remove and re-append the popup to refresh its content
    container.remove();
    activeChangePopup = null;
    console.log("place2");
    const newPopup = createChangeUI(
      change,
      view,
      storage,
      rect.left,
      rect.top,
      selectedCurrentMark,
      selectedNextMark,
      handleSave,
      trackHistorys,
      extension
    );
    document.body.appendChild(newPopup);

    // Set the position based on the captured rect
    newPopup.style.position = "fixed";
    newPopup.style.left = `${rect.left}px`;
    newPopup.style.top = `${rect.top}px`;

    // Clear input
    commentInput.value = "";
    console.log(storage);
    console.log("i am in here..amazing");
    handleSave(true);
  };
  // }
  // if (!extension.options.isTrackingHistoryEnabled) {
    commentsSection.appendChild(addCommentForm);
  // }
  // Buttons container
  const buttonsContainer = document.createElement("div");
  buttonsContainer.className = "change-buttons";
  // if (
  //   trackChangePersonName === USERROLE.ADMIN ||
  //   trackChangePersonName === USERROLE.MARKEL
  // ) {
  const acceptButton = document.createElement("button");
  acceptButton.textContent = "Accept";
  acceptButton.className = "accept-change";
  acceptButton.onclick = () => {
    const { state, dispatch } = view;
    const tr = state.tr;

    // **Set a meta flag to skip plugin processing**
    tr.setMeta(SKIP_TRACK_CHANGES_META, true);

    // Update mark attributes in the document
    const updateMarkAttributes = (from, to, attributes) => {
      state.doc.nodesBetween(from, to, (node, pos) => {
        const mark = node.marks.find((m) => m.type.name === "trackChanges");
        if (mark) {
          const updatedMark = state.schema.marks.trackChanges.create({
            ...mark.attrs,
            ...attributes,
          });
          tr.removeMark(
            pos,
            pos + node.nodeSize,
            state.schema.marks.trackChanges
          );
          tr.addMark(pos, pos + node.nodeSize, updatedMark);
        }
      });
    };
    if (typeof change.ActionBy === "string") {
      change.ActionBy = change.ActionBy.split(",").map((item) => item.trim());
      const role = getUserRoleFromStorage() || "Client";
      if (!change.ActionBy.includes(role)) {
        change.ActionBy.push(role);
      }
    }
    if (!change.ActionBy || change.ActionBy === null) {
      change.ActionBy = getUserRoleFromStorage();
    }
    const newStatus =
      getUserRoleFromStorage() === USERROLE.ADMIN
        ? "Created"
        : getUserRoleFromStorage() === USERROLE.CLIENT
        ? "Pending"
        : "Accepted";
    if (selectedNextMark && selectedCurrentMark) {
      // Find which one has the smaller left position
      const smallerLeftPos = selectedCurrentMark.leftPos
      const largerRightPos = selectedCurrentMark.rightPos
      if (
        selectedCurrentMark.mark.attrs.type === "added" &&
        selectedNextMark.mark.attrs.type === "deleted"
      ) {
        if (changesTextAddedDeleted.length > 0) {
          processTextDeletionsFromSelectedMarks(
            changesTextAddedDeleted,
            selectedCurrentMark,
            selectedNextMark,
            tr,
            areTextAddedDeletedChangesEqual
          );
        }
        // // Remove all marks in the range
        // tr.removeMark(
        //   smallerLeftPos,
        //   largerRightPos,
        //   state.schema.marks.trackChanges
        // );
        // tr.delete(selectedNextMark.leftPos, selectedNextMark.rightPos);
        updateMarkAttributes(
          selectedCurrentMark.leftPos,
          selectedCurrentMark.rightPos,
          {
            Hidden: "false",
            ActionType: "accepted",
            ActionBy: change.ActionBy,
            Status: newStatus,
          }
        );
        // updateMarkAttributes(
        //   selectedNextMark.leftPos,
        //   selectedNextMark.rightPos,
        //   {
        //     Hidden: "true",
        //     ActionType: "accepted",
        //     ActionBy: change.ActionBy,
        //     Status: newStatus,
        //   }
        // );
        const newComment = addCommentToStorage(
          change,
          `Accepted By ${getUserRoleFromStorage()}`
        );
        addHistory(
          change,
          `Accepted By ${getUserRoleFromStorage()}`,
          newComment
        );
      } else if (
        selectedCurrentMark.mark.attrs.type === "deleted" &&
        selectedNextMark.mark.attrs.type === "added"
      ) {
        if (changesTextAddedDeleted.length > 0) {
          processTextDeletionsFromSelectedMarks(
            changesTextAddedDeleted,
            selectedCurrentMark,
            selectedNextMark,
            tr,
            areTextAddedDeletedChangesEqual
          );
        }
        // Remove all marks in the range
        // tr.removeMark(
        //   smallerLeftPos,
        //   largerRightPos,
        //   state.schema.marks.trackChanges
        // );
        // // Remove the deleted text
        // tr.delete(selectedCurrentMark.leftPos, selectedCurrentMark.rightPos);

        updateMarkAttributes(
          selectedCurrentMark.leftPos,
          selectedCurrentMark.rightPos,
          {
            Hidden: "true",
            ActionType: "accepted",
            ActionBy: change.ActionBy,
            Status: newStatus,
          }
        );
        // updateMarkAttributes(
        //   selectedNextMark.leftPos,
        //   selectedNextMark.rightPos,
        //   {
        //     Hidden: "false",
        //     ActionType: "accepted",
        //     ActionBy: change.ActionBy,
        //     Status: newStatus,
        //   }
        // );
        const newComment = addCommentToStorage(
          change,
          `Accepted By ${getUserRoleFromStorage()}`
        );
        addHistory(
          change,
          `Accepted By ${getUserRoleFromStorage()}`,
          newComment
        );
      } else if (
        selectedCurrentMark.mark.attrs.type === "added" &&
        selectedNextMark.mark.attrs.type === "added"
      ) {
        // Remove all marks in the range
        // tr.removeMark(
        //   smallerLeftPos,
        //   largerRightPos,
        //   state.schema.marks.trackChanges
        // );
        if (
          hasSelectionHighlightMarkFound(
            state.doc,
            smallerLeftPos,
            largerRightPos
          )
        ) {
          toast.error("Please resolve comment before accepting track changes.");
          return;
        }
      } else if (
        selectedCurrentMark.mark.attrs.type === "deleted" &&
        selectedNextMark.mark.attrs.type === "deleted"
      ) {
        //tr.delete(smallerLeftPos, largerRightPos);
        if (
          hasSelectionHighlightMarkFound(
            state.doc,
            smallerLeftPos,
            largerRightPos
          )
        ) {
          toast.error("Please resolve comment before accepting track changes.");
          return;
        }
      }
    } else {
      if (change.type === "added") {
        if (changesTextAddedDeleted.length > 0) {
          processTextDeletions(
            changesTextAddedDeleted,
            change,
            tr,
            areTextAddedDeletedChangesEqual
          );
        }
        // Remove the trackChanges mark but keep the text
        // tr.removeMark(
        //   change.from,
        //   change.to,
        //   state.schema.marks.trackChanges
        // );

        //region find the mark in the document and update its attributes
        updateMarkAttributes(change.from, change.to, {
          Hidden: "false",
          ActionType: "accepted",
          ActionBy: change.ActionBy,
          Status: newStatus,
        });

        const newComment = addCommentToStorage(
          change,
          `Accepted By ${getUserRoleFromStorage()}`
        );

        addHistory(
          change,
          `Accepted By ${getUserRoleFromStorage()}`,
          newComment
        );
        // endregion find the mark in the document and update its attributes
      } else if (change.type === "deleted") {
        // **Delete the text without removing the mark**
        // tr.removeMark(
        //   change.from,
        //   change.to,
        //   state.schema.marks.trackChanges
        // );
        // tr.delete(change.from, change.to);

        // Find the mark in the document and update its attributes
        updateMarkAttributes(change.from, change.to, {
          Hidden: "true",
          ActionType: "accepted",
          ActionBy: change.ActionBy,
          Status: newStatus,
        });
        //adding comment to storage
        const newComment = addCommentToStorage(
          change,
          `Accepted By ${getUserRoleFromStorage()}`
        );
        //adding history to trackHistorys
        addHistory(
          change,
          `Accepted By ${getUserRoleFromStorage()}`,
          newComment
        );
      } else if (change.type === "modified") {
        // Replace the original text with new text without the mark
        tr.replaceRangeWith(
          change.from,
          change.to,
          view.state.schema.text(change.newText)
        );
        tr.removeMark(
          change.from,
          change.from + change.newText.length,
          state.schema.marks.trackChanges
        );
        // **Set the cursor position after modification**
        tr.setSelection(
          TextSelection.create(tr.doc, change.from + change.newText.length)
        );
      }
    }

    // **Dispatch the transaction**
    dispatch(tr);

    // Remove the popup
    container.remove();
    activeChangePopup = null;
    console.log("Change accepted", change);
    handleSave(true);
  };

  const hasSelectionHighlightMarkFound = (doc, from, to) => {
    let found = false;

    doc.nodesBetween(from, to, (node) => {
      if (found) return; // Stop further execution
      if (node.marks.some((m) => m.type.name === "selectionHighlight")) {
        found = true;
      }
    });

    return found;
  };

  // Add a comment to storage
  const addCommentToStorage = (
    change,
    text,
    visibilityFor = ["Client", "Markel"]
  ) => {
    const newComment = {
      id: `thread-${Date.now()}`,
      userName: getUserNameFromStorage(),
      timestamp: Date.now(),
      text,
      visibilityFor,
    };

    if (!storage[change.id]) {
      storage[change.id] = [];
    }
    storage[change.id].push(newComment);
    return newComment;
  };

  // Add history to trackHistorys
  const addHistory = (change, status, newComment) => {
    const history = {
      id: `thread-${Date.now()}`,
      type: change.type,
      originalText: change.originalText,
      fullText: change.fullText,
      timestamp: Date.now(),
      status,
      visibilityFor: change.VisibilityFor,
      from: change.from,
      to: change.to,
      historys: newComment,
    };

    if (!trackHistorys[change.id]) {
      trackHistorys[change.id] = [];
    }
    trackHistorys[change.id].push(history);
  };

  const rejectButton = document.createElement("button");
  rejectButton.textContent = "Reject";
  rejectButton.className = "reject-change";
  rejectButton.onclick = () => {
    const { state, dispatch } = view;
    const tr = state.tr;

    // Get the current user ID and name
    //const currentUserId = getUserIdFromStorage();
    const currentUserName = getUserNameFromStorage();

    // Retrieve comments for this change
    const comments = storage[change.id] || [];

    // Check if a comment exists and if the most recent one is by the current user
    const lastComment =
      comments.length > 0 ? comments[comments.length - 1] : null;
    const isLastCommentByCurrentUser =
      lastComment && lastComment.userName === currentUserName;

    // Get reference to error message container in the popup
    let errorMessageContainer = document.querySelector(".reject-error-message");
    if (!errorMessageContainer) {
      errorMessageContainer = document.createElement("div");
      errorMessageContainer.className = "reject-error-message";
      errorMessageContainer.style.color = "#D8000C";
      errorMessageContainer.style.backgroundColor = "#FFD2D2";
      errorMessageContainer.style.padding = "8px";
      errorMessageContainer.style.borderRadius = "5px";
      errorMessageContainer.style.fontSize = "0.9rem";
      errorMessageContainer.style.marginBottom = "10px";
      errorMessageContainer.style.textAlign = "center";
      errorMessageContainer.style.fontWeight = "bold";
      errorMessageContainer.style.display = "none"; // Initially hidden
      container.insertBefore(errorMessageContainer, container.firstChild);
    }

    if (!isLastCommentByCurrentUser) {
      errorMessageContainer.textContent =
        "You must add a comment before rejecting.";
      errorMessageContainer.style.display = "block";
      return;
    }

    // **Set a meta flag to skip plugin processing**
    tr.setMeta(SKIP_TRACK_CHANGES_META, true);

    // Update mark attributes in the document
    const updateMarkAttributes = (from, to, attributes) => {
      state.doc.nodesBetween(from, to, (node, pos) => {
        const mark = node.marks.find((m) => m.type.name === "trackChanges");
        if (mark) {
          const updatedMark = state.schema.marks.trackChanges.create({
            ...mark.attrs,
            ...attributes,
          });
          tr.removeMark(
            pos,
            pos + node.nodeSize,
            state.schema.marks.trackChanges
          );
          tr.addMark(pos, pos + node.nodeSize, updatedMark);
        }
      });
    };
    if (typeof change.ActionBy === "string") {
      change.ActionBy = change.ActionBy.split(",").map((item) => item.trim());
      const role = getUserRoleFromStorage() || "Client";
      if (!change.ActionBy.includes(role)) {
        change.ActionBy.push(role);
      }
    }
    if (!change.ActionBy || change.ActionBy === null) {
      change.ActionBy = getUserRoleFromStorage();
    }
    const newStatus =
      getUserRoleFromStorage() === USERROLE.ADMIN
        ? "Created"
        : getUserRoleFromStorage() === USERROLE.CLIENT
        ? "Pending"
        : "Rejected";

    if (selectedNextMark && selectedCurrentMark) {
      // Find which one has the smaller left position
      const smallerLeftPos = selectedCurrentMark.leftPos
      const largerRightPos = selectedCurrentMark.rightPos

      if (
        selectedCurrentMark.mark.attrs.type === "added" &&
        selectedNextMark.mark.attrs.type === "deleted"
      ) {
        // tr.removeMark(
        //   smallerLeftPos,
        //   largerRightPos,
        //   state.schema.marks.trackChanges
        // );
        // tr.delete(selectedCurrentMark.leftPos, selectedCurrentMark.rightPos);

        updateMarkAttributes(
          selectedCurrentMark.leftPos,
          selectedCurrentMark.rightPos,
          {
            Hidden: "true",
            ActionType: "rejected",
            ActionBy: change.ActionBy,
            Status: newStatus,
          }
        );
        // updateMarkAttributes(
        //   selectedNextMark.leftPos,
        //   selectedNextMark.rightPos,
        //   {
        //     Hidden: "false",
        //     ActionType: "rejected",
        //     ActionBy: change.ActionBy,
        //     Status: newStatus,
        //   }
        // );
        const newComment = addCommentToStorage(
          change,
          `Rejected By ${getUserRoleFromStorage()}`
        );
        addHistory(
          change,
          `Rejected By ${getUserRoleFromStorage()}`,
          newComment
        );
      } else if (
        selectedCurrentMark.mark.attrs.type === "deleted" &&
        selectedNextMark.mark.attrs.type === "added"
      ) {
        // tr.removeMark(
        //   smallerLeftPos,
        //   largerRightPos,
        //   state.schema.marks.trackChanges
        // );
        // tr.delete(selectedNextMark.leftPos, selectedNextMark.rightPos);
        updateMarkAttributes(
          selectedCurrentMark.leftPos,
          selectedCurrentMark.rightPos,
          {
            Hidden: "false",
            ActionType: "rejected",
            ActionBy: change.ActionBy,
            Status: newStatus,
          }
        );
        // updateMarkAttributes(
        //   selectedNextMark.leftPos,
        //   selectedNextMark.rightPos,
        //   {
        //     Hidden: "true",
        //     ActionType: "rejected",
        //     ActionBy: change.ActionBy,
        //     Status: newStatus,
        //   }
        // );
        const newComment = addCommentToStorage(
          change,
          `Rejected By ${getUserRoleFromStorage()}`
        );
        addHistory(
          change,
          `Rejected By ${getUserRoleFromStorage()}`,
          newComment
        );
      } else if (
        selectedCurrentMark.mark.attrs.type === "added" &&
        selectedNextMark.mark.attrs.type === "added"
      ) {
        //tr.delete(smallerLeftPos, largerRightPos);

        if (
          hasSelectionHighlightMarkFound(
            state.doc,
            smallerLeftPos,
            largerRightPos
          )
        ) {
          toast.error("Please resolve comment before rejecting track changes.");
          return;
        }
      } else if (
        selectedCurrentMark.mark.attrs.type === "deleted" &&
        selectedNextMark.mark.attrs.type === "deleted"
      ) {
        // tr.removeMark(
        //   smallerLeftPos,
        //   largerRightPos,
        //   state.schema.marks.trackChanges
        // );

        if (
          hasSelectionHighlightMarkFound(
            state.doc,
            smallerLeftPos,
            largerRightPos
          )
        ) {
          toast.error("Please resolve comment before rejecting track changes.");
          return;
        }
      }
    } else {
      if (change.type === "added") {
        // tr.delete(change.from, change.to);

        updateMarkAttributes(change.from, change.to, {
          Hidden: "true",
          ActionType: "rejected",
          ActionBy: change.ActionBy,
          Status: newStatus,
        });
        const newComment = addCommentToStorage(
          change,
          `Rejected By ${getUserRoleFromStorage()}`
        );
        addHistory(
          change,
          `Rejected By ${getUserRoleFromStorage()}`,
          newComment
        );
        //tr.setSelection(TextSelection.create(tr.doc, change.from));
      } else if (change.type === "deleted") {
        updateMarkAttributes(change.from, change.to, {
          Hidden: "false",
          ActionType: "rejected",
          ActionBy: change.ActionBy,
          Status: newStatus,
        });
        const newComment = addCommentToStorage(
          change,
          `Rejected By ${getUserRoleFromStorage()}`
        );
        addHistory(
          change,
          `Rejected By ${getUserRoleFromStorage()}`,
          newComment
        );
      } else if (change.type === "modified") {
        tr.replaceRangeWith(
          change.from,
          change.to,
          view.state.schema.text(change.originalText)
        );
        tr.removeMark(
          change.from,
          change.from + change.originalText.length,
          state.schema.marks.trackChanges
        );
        tr.setSelection(TextSelection.create(tr.doc, change.from));
      }
    }

    // **Dispatch the transaction**
    dispatch(tr);

    // Remove the popup
    container.remove();
    activeChangePopup = null;
    console.log("Change rejected", change);
    handleSave(true);
  };

  // Event listener to clear error message when typing a comment
  document.addEventListener("input", (event) => {
    if (event.target && event.target.tagName === "TEXTAREA") {
      let errorMessageContainer = document.querySelector(
        ".reject-error-message"
      );
      if (errorMessageContainer) {
        errorMessageContainer.style.display = "none";
      }
    }
  });
  // if (!extension.options.isTrackingHistoryEnabled) {
    buttonsContainer.appendChild(acceptButton);
    buttonsContainer.appendChild(rejectButton);
  // }
  // }

  // Assemble UI
  container.appendChild(closeButton);
  container.appendChild(userInfo);
  container.appendChild(changeDetails);
  // Append visibility section to the container
  // if (
  //   trackChangePersonName === USERROLE.ADMIN &&
  //   !extension.options.isTrackingHistoryEnabled
  // ) {
  //   container.appendChild(visibilitySection);
  // }
  container.appendChild(commentsSection);
  container.appendChild(buttonsContainer);

  // Positioning
  if (left !== null && top !== null) {
    container.style.position = "fixed";
    container.style.left = `${left}px`;
    container.style.top = `${top}px`;
  } else {
    // Default positioning if not provided
    container.style.position = "fixed";
    container.style.left = "100px";
    container.style.top = "100px";
  }

  return container;
}
function areTextAddedDeletedChangesEqual(change1, change2) {
  return (
    change1.from === change2.from && change1.addedText === change2.deletedText
  );
}

function processTextDeletions(
  changesTextAddedDeleted,
  change,
  tr,
  areTextAddedDeletedChangesEqual
) {
  // Get the text between the specified range in the transaction document
  const textBetween = tr.doc.textBetween(change.from, change.to);

  // Split the text into individual characters and map them to changes
  const splitedArrayText = textBetween.split("").map((char, index) => ({
    from: change.from + index,
    to: change.from + index,
    addedText: "",
    deletedText: char,
  }));

  // Iterate through the split text changes and check for overlaps
  splitedArrayText.forEach((textChange) => {
    const isSame = changesTextAddedDeleted.some((existingChange) =>
      areTextAddedDeletedChangesEqual(existingChange, textChange)
    );

    // Remove the overlapping change from the array if it exists
    if (isSame) {
      const index = changesTextAddedDeleted.findIndex((existingChange) =>
        areTextAddedDeletedChangesEqual(existingChange, textChange)
      );
      if (index !== -1) {
        changesTextAddedDeleted.splice(index, 1);
      }
    }
  });

  // Debugging logs (optional)
  console.log(splitedArrayText, "splitedArrayText");
  console.log(textBetween, "textBetween");
  console.log(changesTextAddedDeleted, "changesTextAddedDeleted");
}

function processTextDeletionsFromSelectedMarks(
  changesTextAddedDeleted,
  selectedCurrentMark,
  selectNextMark,
  tr,
  areTextAddedDeletedChangesEqual
) {
  // Get the text between the specified range in the transaction document
  let textBetween = null;
  if (selectedCurrentMark.mark.attrs.type === "added") {
    textBetween = tr.doc.textBetween(
      selectNextMark.leftPos,
      selectNextMark.rightPos
    );
  } else if (selectNextMark.mark.attrs.type === "added") {
    textBetween = tr.doc.textBetween(
      selectedCurrentMark.leftPos,
      selectedCurrentMark.rightPos
    );
  }
  // Split the text into individual characters and map them to changes
  const splitedArrayText = textBetween.split("").map((char, index) => ({
    from:
      selectedCurrentMark.mark.attrs.type === "added"
        ? selectedCurrentMark.leftPos + index
        : selectNextMark.leftPos + index,
    to:
      selectedCurrentMark.mark.attrs.type === "added"
        ? selectedCurrentMark.leftPos + index
        : selectNextMark.left + index,
    addedText: "",
    deletedText: char,
  }));

  // Iterate through the split text changes and check for overlaps
  splitedArrayText.forEach((textChange) => {
    const isSame = changesTextAddedDeleted.some((existingChange) =>
      areTextAddedDeletedChangesEqual(existingChange, textChange)
    );

    // Remove the overlapping change from the array if it exists
    if (isSame) {
      const index = changesTextAddedDeleted.findIndex((existingChange) =>
        areTextAddedDeletedChangesEqual(existingChange, textChange)
      );
      if (index !== -1) {
        changesTextAddedDeleted.splice(index, 1);
      }
    }
  });
}

export const TrackChanges = Mark.create({
  name: "trackChanges",

  addProseMirrorMark() {
    return {
      inclusive: false, // Prevent the cursor from being included within the mark
    };
  },

  addOptions() {
    applyTrackChangesStyles();

    return {
      HTMLAttributes: {
        class: "track-changes",
      },
      isTrackingEnabled: true, // Enable tracking by default
      getUserId: getUserIdFromStorage,
      getUserName: getUserNameFromStorage,
      onChangeAccept: (change) => {
        console.log("Change accepted:", change);
      },
      onChangeReject: (change) => {
        console.log("Change rejected:", change);
      },
      // Callback for comment updates (optional)
      onCommentUpdate: (change) => {},
      trackChangesComments: {},
      handleSave: null,
      trackHistorys: {},
    };
  },

  addStorage() {
    console.log("in add storage.");
    console.log(this.options.trackChangesComments, "STORAGE");
    return {
      trackChangesComments: this.options.trackChangesComments,
      trackHistorys: this.options.trackHistorys,
    };
  },

  addAttributes() {
    return {
      type: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-change-type"),
        renderHTML: (attributes) => ({
          "data-change-type": attributes.type,
        }),
      },
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-change-id"),
        renderHTML: (attributes) => ({
          "data-change-id": attributes.id,
        }),
      },
      userId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-user-id"),
        renderHTML: (attributes) => ({
          "data-user-id": attributes.userId,
        }),
      },
      userName: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-user-name"),
        renderHTML: (attributes) => ({
          "data-user-name": attributes.userName,
        }),
      },
      timestamp: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-timestamp"),
        renderHTML: (attributes) => ({
          "data-timestamp": attributes.timestamp,
        }),
      },
      originalText: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-original-text"),
        renderHTML: (attributes) => ({
          "data-original-text": attributes.originalText,
        }),
      },
      newText: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-new-text"),
        renderHTML: (attributes) => ({
          "data-new-text": attributes.newText,
        }),
      },
      VisibilityFor: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-visibility-for"),
        renderHTML: (attributes) => ({
          "data-visibility-for": attributes.VisibilityFor,
        }),
      },
      Hidden: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-hidden"),
        renderHTML: (attributes) => ({
          "data-hidden": attributes.Hidden,
        }),
      },
      ActionType: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-action-type"),
        renderHTML: (attributes) => ({
          "data-action-type": attributes.ActionType,
        }),
      },
      ActionBy: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-action-by"),
        renderHTML: (attributes) => ({
          "data-action-by": attributes.ActionBy,
        }),
      },
      Status: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-status"),
        renderHTML: (attributes) => ({
          "data-status": attributes.Status,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-change-type]",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = { ...HTMLAttributes };

    // Ensure class is always present
    attrs.class = attrs.class || "track-changes";

    // Apply type-specific classes
    switch (attrs["data-change-type"]) {
      case "added":
        attrs.class = `track-changes added ${attrs.class}`.trim();
        break;
      case "deleted":
        attrs.class = `track-changes deleted ${attrs.class}`.trim();
        break;
      case "modified":
        attrs.class = `track-changes modified ${attrs.class}`.trim();
        break;
    }
    return ["span", attrs, 0];
  },

  addCommands() {
    return {
      // Enhanced commands to track changes
      trackAdd:
        (text) =>
        ({ tr, state, commands }) => {
          const { isTrackingEnabled, getUserId, getUserName } = this.options;
          if (!isTrackingEnabled) return true;

          const userId = getUserId();
          const userName = getUserName();
          const userRole = getUserRoleFromStorage();

          return commands.setMark(this.name, {
            type: "added",
            id: `change-${Date.now()}`,
            userId,
            userName,
            userRole,
            timestamp: Date.now(),
            originalText: text,
          });
        },

      trackDelete:
        (text) =>
        ({ tr, state, commands }) => {
          const { isTrackingEnabled, getUserId, getUserName } = this.options;
          if (!isTrackingEnabled) return true;

          const userId = getUserId();
          const userName = getUserName();
          const userRole = getUserRoleFromStorage();

          return commands.setMark(this.name, {
            type: "deleted",
            id: `change-${Date.now()}`,
            userId,
            userName,
            userRole,
            timestamp: Date.now(),
            originalText: text,
          });
        },

      trackModify:
        (oldText, newText) =>
        ({ tr, state, commands }) => {
          const { isTrackingEnabled, getUserId, getUserName } = this.options;
          if (!isTrackingEnabled) return true;

          const userId = getUserId();
          const userName = getUserName();
          const userRole = getUserRoleFromStorage();

          return commands.setMark(this.name, {
            type: "modified",
            id: `change-${Date.now()}`,
            userId,
            userName,
            userRole,
            timestamp: Date.now(),
            originalText: oldText,
            newText: newText,
          });
        },
    };
  },

  addProseMirrorPlugins() {
    const pluginKey = new PluginKey("trackChanges");
    const extension = this;

    const handleTrackChangesMerge = (
      tr,
      newState,
      step,
      addedText,
      deletedText,
      userId,
      userName,
      storage,
      oldState
    ) => {
      const start = step.from;
      const end = step.from + step.slice.content.size;
      let trackedChanges = false;

      // Function to get the track changes mark at a specific position
      const getTrackChangeMark = (pos) => {
        try {
          console.log("pos from getTrackChangeMark", pos);
          const resolvedPos = newState.doc.resolve(pos);
          console.log("resolvedPos", resolvedPos);
          const nodeAtPos = resolvedPos.nodeAfter || resolvedPos.nodeBefore;
          console.log("nodeAtPos", nodeAtPos);
          if (!nodeAtPos) return undefined;
          // const mark= nodeAtPos.marks.find((m) => m.type.name === "trackChanges");
          // if(mark &&  mark.attrs.ActionType === TRACKCHANGE.ACTION.CREATED){
          //  return undefined;
          // }
          console.log(
            "nodeAtPos marks",
            nodeAtPos.marks.find((m) => m.type.name === "trackChanges")
          );
          return nodeAtPos.marks.find(
            (m) =>
              m.type.name === TRACKCHANGE.NAME &&
              m.attrs.ActionType === TRACKCHANGE.ACTION.CREATED
          );
        } catch (error) {
          console.error("Error in getTrackChangeMark:", error);
          return undefined;
        }
      };
      // Handle tracking for added text
      const handleAddedText = () => {
        const changeTextAddedDetails = {
          from: step.from,
          to: step.to,
          deletedText,
          addedText,
        };
        console.log("changeTextAddedDetails", changeTextAddedDetails);
        changesTextAddedDeleted.push(changeTextAddedDetails);

        const { leftPos, rightPos } = findContinuousMarksExactRange(start);
        console.log(leftPos, "leftPos", rightPos, "rightPos");
        // Collect continuous marks
        const continuousMarks = [];
        for (let i = leftPos; i <= rightPos; i++) {
          const mark = getTrackChangeMark(i);
          if (
            mark &&
            mark.attrs.type === TRACKCHANGE.CHANGETYPE.ADDED &&
            mark.attrs.ActionType === TRACKCHANGE.ACTION.CREATED
          ) {
            continuousMarks.push({ pos: i, mark });
          }
        }

        console.log(continuousMarks, "continuousMarks");
        console.log(continuousMarks.length, "continuousMarks");
        // If continuous marks are found
        if (continuousMarks.length > 0) {
          // Safely extract first mark's attributes
          const firstMarkAttrs = continuousMarks[0].mark.attrs || {};

          // Collect text of all continuous marks and the new text
          let mergedText = continuousMarks
            .map((m) => newState.doc.textBetween(m.pos, m.pos + 1))
            .join("");
          //+ addedText

          // Remove all existing continuous marks
          tr.removeMark(
            leftPos,
            rightPos + 1,
            newState.schema.marks.trackChanges
          );
          console.log(mergedText, "mergedText");
          // Add a new merged mark
          tr.addMark(
            leftPos,
            leftPos + mergedText.length,
            newState.schema.marks.trackChanges.create({
              type: "added",
              id: `change-${firstMarkAttrs.timestamp || Date.now()}`,
              userId: firstMarkAttrs.userId || userId,
              userName: firstMarkAttrs.userName || userName,
              userRole: firstMarkAttrs.userRole || getUserRoleFromStorage(),
              timestamp: Math.min(
                ...continuousMarks.map(
                  (m) => m.mark.attrs?.timestamp || Date.now()
                ),
                Date.now()
              ),
              originalText: mergedText,
              VisibilityFor: ["Client", "Markel"],
              Hidden: "",
              ActionType: "Created",
              ActionBy: getUserRoleFromStorage(),
              Status: "Created",
            })
          );
          trackedChanges = true;
        }
        // If no continuous marks, create a new mark
        else {
          tr.addMark(
            start,
            end,
            newState.schema.marks.trackChanges.create({
              type: "added",
              id: `change-${Date.now()}`,
              userId,
              userName,
              userRole: getUserRoleFromStorage(),
              timestamp: Date.now(),
              originalText: addedText,
              VisibilityFor: ["Client", "Markel"],
              Hidden: "",
              ActionType: "Created",
              ActionBy: userName,
              Status: "Created",
            })
          );
          trackedChanges = true;
        }
      };

      // Handle tracking for deleted text
      const handleDeletedText = () => {
        // Begining of Handle recently added text and  then deleted
        let textDelete = "";
        // Handle deleted text longer than one character
        if (deletedText.length > 1) {
          const indexedText = Array.from(deletedText).map((char, index) => ({
            from: step.from + index,
            to: step.from + index + 1,
            deletedText: char,
            addedText: "",
          }));
          console.log("Indexed Deleted Text:", indexedText);
          indexedText.forEach((textChange) => {
            const isSame = changesTextAddedDeleted.some((change) =>
              areTextAddedDeletedChangesEqual(change, textChange)
            );
            if (isSame) {
              tr.delete(textChange.from, textChange.from);
              const index = changesTextAddedDeleted.findIndex((change) =>
                areTextAddedDeletedChangesEqual(change, textChange)
              );
              if (index !== -1) {
                changesTextAddedDeleted.splice(index, 1);
              }
              trackedChanges = true;
            } else {
              textDelete += textChange.deletedText;
            }
            deletedText = textDelete;
          });
        } else {
          const changeTextDeletedDetails = {
            from: step.from,
            to: step.to,
            deletedText,
            addedText,
          };
          console.log("changeTextDeletedDetails", changeTextDeletedDetails);
          const isTextAddedDeletedSame = changesTextAddedDeleted.some(
            (change) =>
              areTextAddedDeletedChangesEqual(change, changeTextDeletedDetails)
          );
          console.log(
            "The changeTextDeletedDetails is ",
            isTextAddedDeletedSame,
            "For",
            changeTextDeletedDetails
          );
          if (isTextAddedDeletedSame) {
            tr.delete(step.from, step.from);
            const index = changesTextAddedDeleted.findIndex((change) =>
              areTextAddedDeletedChangesEqual(change, changeTextDeletedDetails)
            );
            if (index !== -1) {
              changesTextAddedDeleted.splice(index, 1);
            }
            trackedChanges = true;
            return;
          }
        }
        // End of  Handle recently added text and  then deleted
        // Re-add the deleted text to preserve it
        console.log(deletedText, step.from, "deleted", "stepFrom");
        tr.insertText(deletedText, step.from, step.from);

        // Find the exact range of continuous deleted marks
        const { leftPos, rightPos } = findContinuousMarksExactRange(
          start,
          "deleted"
        );

        tr.setSelection(TextSelection.create(tr.doc, step.from));

        console.log(
          leftPos,
          "leftPos form delete",
          rightPos,
          "rightPos from delete"
        );
        // Collect continuous marks
        const continuousMarks = [];
        for (let i = leftPos; i <= rightPos; i++) {
          const mark = getTrackChangeMark(i);
          console.log(mark, "mark");
          if (mark && mark.attrs.type === "deleted") {
            continuousMarks.push({ pos: i, mark });
          }
        }

        console.log(continuousMarks, "continuousMarks from delete");
        console.log(
          continuousMarks.length,
          "continuousMarks length from delete"
        );
        // If continuous marks are found
        if (continuousMarks.length > 0) {
          // Safely extract first mark's attributes
          const firstMarkAttrs = continuousMarks[0].mark.attrs || {};

          // Collect text of all continuous marks and the new deleted text
          let mergedText =
            continuousMarks
              .map((m) => newState.doc.textBetween(m.pos, m.pos + 1))
              .join("") + deletedText;
          console.log(mergedText, "mergedText from delete");
          // Remove all existing continuous marks
          tr.removeMark(
            leftPos,
            rightPos + 1,
            newState.schema.marks.trackChanges
          );
          // Add a new merged mark
          tr.addMark(
            leftPos,
            leftPos + mergedText.length,
            newState.schema.marks.trackChanges.create({
              type: "deleted",
              id: `change-${firstMarkAttrs.timestamp || Date.now()}`,
              userId: firstMarkAttrs.userId || userId,
              userName: firstMarkAttrs.userName || userName,
              timestamp: Math.min(
                ...continuousMarks.map(
                  (m) => m.mark.attrs?.timestamp || Date.now()
                ),
                Date.now()
              ),
              originalText: mergedText,
            })
          );
          console.log("mark added from continous delete at", leftPos);
          console.log(tr, "tr from delete");
          trackedChanges = true;
        }
        // If no continuous marks, create a new mark
        else {
          tr.addMark(
            start,
            start + deletedText.length,
            newState.schema.marks.trackChanges.create({
              type: "deleted",
              id: `change-${Date.now()}`,
              userId,
              userName,
              timestamp: Date.now(),
              originalText: deletedText,
            })
          );
          console.log("mark added from delete at", start);
          console.log(tr, "tr from delete");
          trackedChanges = true;
        }
      };

      // Function to find the exact range of continuous marks
      const findContinuousMarksExactRange = (start, typeFilter = null) => {
        let leftPos = start;
        let rightPos = start;

        // Capture the initial mark's user details
        const initialMark = getTrackChangeMark(start);
        console.log(initialMark, "initialMark");
        if (!initialMark) return { leftPos, rightPos };

        const initialType = initialMark.attrs.type;
        const initialUserId = initialMark.attrs.userId;
        const initialUserName = initialMark.attrs.userName;

        // If a type filter is provided, ensure it matches
        if (typeFilter && initialType !== typeFilter) {
          return { leftPos, rightPos };
        }

        // Move left to find the start of continuous marks
        while (leftPos > 0) {
          const markBefore = getTrackChangeMark(leftPos - 1);
          console.log("markBefore", markBefore);
          // Stop if no mark or mark differs from initial
          if (
            !markBefore ||
            markBefore.attrs.type !== initialType ||
            markBefore.attrs.userId !== initialUserId ||
            markBefore.attrs.userName !== initialUserName ||
            (typeFilter && markBefore.attrs.type !== typeFilter)
          ) {
            console.log("break from leftPos");
            break;
          }

          leftPos--;
        }

        // Move right to find the end of continuous marks
        while (rightPos < newState.doc.content.size) {
          const markAfter = getTrackChangeMark(rightPos + 1);

          // Stop if no mark or mark differs from initial
          if (
            !markAfter ||
            markAfter.attrs.type !== initialType ||
            markAfter.attrs.userId !== initialUserId ||
            markAfter.attrs.userName !== initialUserName ||
            (typeFilter && markAfter.attrs.type !== typeFilter)
          ) {
            console.log("break from rightPos");
            break;
          }

          rightPos++;
        }
        console.log("leftPos from fun", leftPos, "rightPos from fun", rightPos);
        return { leftPos, rightPos };
      };

      // Handle tracking for added content and add merge logic
      if (addedText && !deletedText) {
        handleAddedText();
      }

      // if (deletedText) {
      //   handleDeletedText()
      // }

      return trackedChanges;
    };

    return [
      new Plugin({
        key: pluginKey,
        state: {
          init() {
            return {
              trackChangesComments: extension.storage.trackChangesComments,
              trackHistorys: extension.storage.trackHistorys,
            };
          },
          apply(tr, pluginState) {},
        },

        // Modification to the appendTransaction method in the track changes plugin
        appendTransaction: (transactions, oldState, newState) => {
          // **Check for the 'skipTrackChanges' meta flag**
          const hasSkip = transactions.some((tr) =>
            tr.getMeta(SKIP_TRACK_CHANGES_META)
          );
          if (hasSkip || !extension.options.isTrackingEnabled) return null;

          const tr = newState.tr;
          let trackedChanges = false;
          console.log(oldState, "OldState", newState, "NewState");
          transactions.forEach((transaction) => {
            if (transaction.docChanged) {
              const userId = extension.options.getUserId();
              const userName = extension.options.getUserName();

              transaction.steps.forEach((step) => {
                if (step instanceof ReplaceStep) {
                  // Skip if the step covers the entire document
                  if (
                    step.from === 0 &&
                    step.to === newState.doc.content.size
                  ) {
                    return;
                  }

                  const addedText = step.slice.content.textBetween(
                    0,
                    step.slice.content.size
                  );
                  const deletedText = oldState.doc.textBetween(
                    step.from,
                    step.to
                  );

                  console.log("Change details:", {
                    from: step.from,
                    to: step.to,
                    deletedText,
                    addedText,
                  });

                  trackedChanges = handleTrackChangesMerge(
                    tr,
                    newState,
                    step,
                    addedText,
                    deletedText,
                    userId,
                    userName,
                    extension.storage.trackChangesComments, // Pass storage
                    oldState
                  );
                }
              });
            }
          });

          return trackedChanges ? tr : null;
        },

        props: {
          handleClick(view, pos, event) {
            console.log("First");
            const { trackChangeDisplayBy, isTrackingHistoryEnabled } =
              extension.options;
            console.log(trackChangeDisplayBy, "trackChangeBy");
            console.log(isTrackingHistoryEnabled, "isTrackingHistoryEnabled");
            trackChangePersonName =
              trackChangeDisplayBy ?? trackChangePersonName;
            if (
              trackChangePersonName === USERROLE.SUBCONTRACTOR ||
              !extension.options.isTrackingEnabled
            ) {
              return false;
            }

            const { state } = view;
            const { schema } = state;

            // Find the exact mark at the clicked position
            const foundMarks = [];
            let startPos = pos;

            console.log("pos = " + pos);
            console.log("State = ", state);
            state.doc.nodesBetween(pos, pos, (node, nodePos) => {
              if (node.marks.length > 0) {
                const trackChangeMark = node.marks.find(
                  (m) => m.type.name === "trackChanges"
                );
                console.log("trackChangeMark = ", trackChangeMark);
                if (trackChangeMark) {
                  foundMarks.push({
                    mark: trackChangeMark,
                    node: node,
                    nodePos: nodePos, // Store the start position of the node
                  });
                  // Update startPos to the beginning of the node
                  startPos = nodePos;
                }
              }
            });

            // If no marks found, or tracking is disabled, do nothing
            if (
              foundMarks.length === 0 ||
              !extension.options.isTrackingEnabled
            ) {
              return false;
            }

            // Use the first found mark (in case of multiple)
            const { mark: foundMark, node: foundNode, nodePos } = foundMarks[0];

            if (
              foundMark.attrs.Hidden === TRACKCHANGE.HIDDENTYPE.FALSE &&
              !isTrackingHistoryEnabled
            ) {
              return false;
            }
            //check mark before nodePos and nodePos + (foundNode?.nodeSize || 0)

            const selectedCurrentMark = {
              ...foundMarks[0],
              leftPos: nodePos,
              rightPos: nodePos + (foundNode?.nodeSize || 0),
            };
            console.log("selectedCurrentMark", selectedCurrentMark);

            // Helper function to find a trackChanges mark at a given position
            const findTrackChangeMark = (startPos) => {
              let foundMark = null;
              state.doc.nodesBetween(startPos, startPos, (node, nodePos) => {
                const mark = node.marks.find(
                  (m) => m.type.name === "trackChanges"
                );
                if (mark) {
                  foundMark = {
                    mark,
                    node,
                    nodePos,
                    leftPos: nodePos,
                    rightPos: nodePos + (node?.nodeSize || 0),
                  };
                }
              });
              return foundMark;
            };
            // Find marks before and after the selected node position every mark node is in differnt different node
            const selectedNextMark =
              findTrackChangeMark(selectedCurrentMark.leftPos - 1) ||
              findTrackChangeMark(selectedCurrentMark.rightPos + 1);
            console.log("selectedCurrentMark after", selectedNextMark);

            // If activeChangePopup exists and click is on the same mark, do nothing
            if (activeChangePopup) {
              const existingMark =
                activeChangePopup.getAttribute("data-change-id");
              if (existingMark === foundMark.attrs.id) {
                return false;
              }
            }
            console.log("place3");
            console.log(extension.storage);
            console.log(extension.storage?.trackChangesComments);
            console.log(foundMark.attrs, "FoundMark");

            const changeUI = createChangeUI(
              {
                ...foundMark.attrs,
                // Add full text context
                fullText: foundNode.text || "",
                from: nodePos,
                to: nodePos + (foundNode?.nodeSize || 0),
              },
              view,
              extension.storage.trackChangesComments, // Pass storage
              null,
              null,
              selectedCurrentMark,
              selectedNextMark,
              extension.options.handleSave,
              extension.storage.trackHistorys,
              extension
            );

            // Add a data attribute to track the change ID
            changeUI.setAttribute("data-change-id", foundMark.attrs.id);

            document.body.appendChild(changeUI);
            activeChangePopup = changeUI;

            // Position the popup based on the event's target
            const rect = event.target.getBoundingClientRect();
            changeUI.style.position = "fixed";
            changeUI.style.left = `${rect.right + 10}px`;
            changeUI.style.top = `${rect.top}px`;

            return false;
          },
          handleKeyDown(view, event) {
            if (!extension.options.isTrackingEnabled) {
              return false;
            }

            if (
              (event.key === "Backspace" && !event.shiftKey) ||
              (event.key === "Delete" && !event.shiftKey)
            ) {
              const { state, dispatch } = view;
              const { from, to } = state.selection;
              if (from === to) {
                if (event.key === "Backspace" && from > 0) {
                  //handleDelete(view, from - 1, from)
                  const deletedChar = state.doc.textBetween(from - 1, from);
                  const changeTextDeletedDetails = {
                    from: from - 1,
                    to: from,
                    deletedText: deletedChar,
                    addedText: "",
                  };
                  console.log(
                    "changeTextDeletedDetails",
                    changeTextDeletedDetails
                  );
                  const isTextAddedDeletedSame = changesTextAddedDeleted.some(
                    (change) =>
                      areTextAddedDeletedChangesEqual(
                        change,
                        changeTextDeletedDetails
                      )
                  );
                  console.log(
                    "The changeTextDeletedDetails is ",
                    isTextAddedDeletedSame,
                    "For",
                    changeTextDeletedDetails
                  );
                  if (isTextAddedDeletedSame) {
                    const index = changesTextAddedDeleted.findIndex((change) =>
                      areTextAddedDeletedChangesEqual(
                        change,
                        changeTextDeletedDetails
                      )
                    );
                    if (index !== -1) {
                      changesTextAddedDeleted.splice(index, 1);
                    }
                    return false;
                  } else {
                    handleDelete(view, from - 1, from);
                  }
                } else if (
                  event.key === "Delete" &&
                  to < state.doc.content.size
                ) {
                  //handleDelete(view, to, to + 1)
                  const deletedChar = state.doc.textBetween(to, to + 1);
                  const changeTextDeletedDetails = {
                    from: to,
                    to: to + 1,
                    deletedText: deletedChar,
                    addedText: "",
                  };
                  console.log(
                    "changeTextDeletedDetails",
                    changeTextDeletedDetails
                  );
                  const isTextAddedDeletedSame = changesTextAddedDeleted.some(
                    (change) =>
                      areTextAddedDeletedChangesEqual(
                        change,
                        changeTextDeletedDetails
                      )
                  );
                  console.log(
                    "The changeTextDeletedDetails is ",
                    isTextAddedDeletedSame,
                    "For",
                    changeTextDeletedDetails
                  );
                  if (isTextAddedDeletedSame) {
                    const index = changesTextAddedDeleted.findIndex((change) =>
                      areTextAddedDeletedChangesEqual(
                        change,
                        changeTextDeletedDetails
                      )
                    );
                    if (index !== -1) {
                      changesTextAddedDeleted.splice(index, 1);
                    }
                    return false;
                  } else {
                    handleDelete(view, to, to + 1);
                  }
                }
              } else {
                handleDelete(view, from, to);
              }
              // Prevent the default delete behavior
              event.preventDefault();
              return true;
            }

            return false;
          },
          handleTextInput(view, from, to, text) {
            const { state, dispatch } = view;
            const tr = state.tr;
            if (!extension.options.isTrackingEnabled) {
              // Ensure the new text is added as plain text outside any existing marks
              const nodeBefore = state.doc.nodeAt(from - 1);
              const nodeAfter = state.doc.nodeAt(to);

              // Check for adjacent marks
              const markBefore = nodeBefore?.marks.find(
                (m) => m.type.name === TRACKCHANGE.NAME
              );
              const markAfter = nodeAfter?.marks.find(
                (m) => m.type.name === TRACKCHANGE.NAME
              );
              if (markBefore || markAfter) {
                tr.insertText(text, from);
                tr.removeMark(
                  from,
                  from + text.length,
                  state.schema.marks.trackChanges
                );
                dispatch(tr);
                return true;
              }
              return false;
            }
            if (from !== to && text.length > 0) {
              const selectedText = state.doc.textBetween(from, to, " ");
              console.log("Selected Text:", selectedText); // This is the selected text
              changesTextAddedDeleted.push({
                from: from,
                to: from,
                deletedText: "",
                addedText: text,
              });
              // Step 1: Insert the new text BEFORE the selected (deleted) text
              tr.insertText(text, from); // Insert at 'from' position
              tr.addMark(
                from,
                from + text.length,
                state.schema.marks.trackChanges.create({
                  type: "added",
                  id: `change-${Date.now()}`,
                  userId: getUserIdFromStorage(),
                  userName: getUserNameFromStorage(),
                  userRole: getUserRoleFromStorage(),
                  timestamp: Date.now(),
                  originalText: selectedText,
                  newText: text,
                  VisibilityFor: [
                    TRACKCHANGE.VISIBILITYFOR.CLIENT,
                    TRACKCHANGE.VISIBILITYFOR.MARKEL,
                  ],
                  Hidden: "",
                  ActionType: "Created",
                  ActionBy: getUserRoleFromStorage(),
                  Status: "Created",
                })
              );

              // Step 2: Mark the original selected text as "deleted"
              tr.addMark(
                from + text.length,
                to + text.length,
                state.schema.marks.trackChanges.create({
                  type: "deleted",
                  id: `change-${Date.now()}`,
                  userId: getUserIdFromStorage(),
                  userName: getUserNameFromStorage(),
                  userRole: getUserRoleFromStorage(),
                  timestamp: Date.now(),
                  originalText: selectedText,
                  newText: text,
                  VisibilityFor: [
                    TRACKCHANGE.VISIBILITYFOR.CLIENT,
                    TRACKCHANGE.VISIBILITYFOR.MARKEL,
                  ],
                  Hidden: "",
                  ActionType: "Created",
                  ActionBy: getUserRoleFromStorage(),
                  Status: "Created",
                })
              );

              // Step 3: Move the cursor to the end of the newly added text
              tr.setSelection(TextSelection.create(tr.doc, from + text.length));
              dispatch(tr);
              return true;
            }
          },
        },
      }),
    ];
  },
});

const handleDelete = (view, from, to) => {
  const { state, dispatch } = view;
  const { schema, doc } = state;
  let tr = state.tr;
  const deletedChar = doc.textBetween(from, to);

  // Function to get the 'deleted' mark at a specific position
  const getDeleteMarkAt = (doc, pos) => {
    const node = doc.nodeAt(pos);
    if (!node) return null;

    return node.marks.find(
      (m) =>
        m.type.name === "trackChanges" &&
        m.attrs.type === "deleted" &&
        m.attrs.ActionType === TRACKCHANGE.ACTION.CREATED
    );
  };

  function expandMarkBounds(doc, from, to, getDeleteMarkAt) {
    let markStart = from;
    let markEnd = to;

    // Decrease markStart until no mark is found
    while (markStart > 0 && getDeleteMarkAt(doc, markStart - 1)) {
      markStart -= 1;
    }

    // Increase markEnd until no mark is found
    while (markEnd < doc.content.size && getDeleteMarkAt(doc, markEnd)) {
      markEnd += 1;
    }

    return { markStart, markEnd };
  }
  // Expand the mark bounds
  const { markStart, markEnd } = expandMarkBounds(
    doc,
    from,
    to,
    getDeleteMarkAt
  );
  console.log(markStart, "markStart", markEnd, "markEnd");
  // Check for adjacent deletion marks
  const beforeMark = markStart > 0 ? getDeleteMarkAt(doc, from - 1) : null;
  const afterMark =
    markEnd < doc.content.size ? getDeleteMarkAt(doc, to) : null;

  if (beforeMark || afterMark) {
    // Merge with existing deletion mark
    const existingMark = beforeMark || afterMark;
    console.log(existingMark, "existingMark");

    // Remove existing mark
    tr = tr.removeMark(markStart, markEnd, schema.marks.trackChanges);

    // Create new combined mark
    const newMark = schema.marks.trackChanges.create({
      ...existingMark.attrs,
      originalText: doc.textBetween(markStart, markEnd),
    });

    // Add combined mark
    tr = tr.addMark(markStart, markEnd, newMark);
  } else {
    // Create new deletion mark
    const deleteMark = schema.marks.trackChanges.create({
      type: "deleted",
      id: `change-${Date.now()}`,
      userId: getUserIdFromStorage(),
      userName: getUserNameFromStorage(),
      userRole: getUserRoleFromStorage(),
      timestamp: Date.now(),
      originalText: doc.textBetween(from, to),
      VisibilityFor: [
        TRACKCHANGE.VISIBILITYFOR.CLIENT,
        TRACKCHANGE.VISIBILITYFOR.MARKEL,
      ],
      Hidden: "",
      ActionType: "Created",
      ActionBy: getUserRoleFromStorage(),
      Status: "Created",
    });

    // Add the mark
    tr = tr.addMark(from, to, deleteMark);
  }
  tr = tr.setSelection(TextSelection.create(tr.doc, from));
  dispatch(tr);
};

export function applyTrackChangesStyles() {
  const style = document.createElement("style");
  style.textContent = `
    .track-changes {
      transition: background-color 0.3s ease;
    }
    
    .track-changes.added {
      background-color: #90EE9088;
      text-decoration: underline green;
      cursor: pointer;
    }
    
    .track-changes.deleted {
      background-color: #FF000088;
      text-decoration: line-through red;
      cursor: pointer;
    }
    
    .track-changes.modified {
      background-color: #87CEFA88;
      text-decoration: underline blue;
      cursor: pointer;
    }
    
    .track-changes-container {
      position: fixed;
      background: white;
      border: 1px solid #ccc;
      border-radius: 8px;
      box-shadow: 0 4px 8px rgba(0,0,0,0.2);
      padding: 20px;
      z-index: 999999999;
      max-width: 90vw; 
      min-width: 400px; 
      font-size: 0.8rem;
      max-height: 80vh;
      overflow-y: auto;
      top:0 !important;
      left:50% !important;
      transform: translateX(-50%) !important;
       overflow-x: auto; 
    }
    
    .change-user-info {
      font-size: 1rem;
      color: #666;
      margin-bottom: 12px;
      padding: 8px;
      border-bottom: 1px solid #ddd;
    }
    
    .change-details {
      font-size: 1.1rem;
      margin-bottom: 16px;
      padding: 8px;
      background-color: #f9f9f9;
      border-radius: 6px;
    }
    
    .comments-section {
      margin-top: 16px;
      border-top: 1px solid #ddd;
      padding-top: 12px;
    }

    .comments-section h4 {
      margin-bottom: 8px;
      font-size: 1.1rem;
    }

    .comments-list {
      max-height: auto;
      overflow-y: auto;
      margin-bottom: 12px;
    }

    .comment-thread {
      border-bottom: 1px solid #eee;
      padding: 8px 0;
    }

    .comment-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;
    }

    .comment-header strong {
      font-weight: bold;
    }

    .comment-date {
      font-size: 0.8em;
      color: #999;
    }

    .comment-text {
      margin-bottom: 4px;
      white-space: pre-wrap;
    }

    .add-comment-form {
      display: flex;
      flex-direction: column;
    }

    .add-comment-form textarea {
      resize: vertical;
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 1rem;
      margin-bottom: 8px;
    }

    .add-comment-button {
      align-self: flex-end;
      padding: 8px 12px;
      margin-bottom: 5px;
      background-color: #007BFF;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      transition: background-color 0.3s;
    }

    .add-comment-button:hover {
      background-color: #0056b3;
    }

    .change-buttons {
      display: flex;
      justify-content: space-between;
    }
    
    .change-buttons button {
      padding: 10px 16px;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 1rem;
    }
    
    .accept-change {
      background-color: #4CAF50;
      color: white;
      transition: background-color 0.3s;
    }
    
    .accept-change:hover {
      background-color: #45a049;
    }
    
    .reject-change {
      background-color: #F44336;
      color: white;
      transition: background-color 0.3s;
    }
    
    .reject-change:hover {
      background-color: #e53935;
    }
    .close-button {
      position: absolute;
      top: 8px;
      right: 8px;
      background: transparent;
      border: none;
      font-size: 1.5rem;
      font-weight: bold;
      cursor: pointer;
      color: #666;
      transition: color 0.3s ease;
    }

    .close-button:hover {
      color: #000;
    }
      .visibility-section {
      margin-bottom: 16px;
    }

    .visibility-section h4 {
      font-size: 1.2rem;
      margin-bottom: 8px;
    }

    .visibility-section input[type="checkbox"],
.comment-visibility-section input[type="checkbox"] {

transform: scale(1.2);
margin-right: 8px;
}

    .visibility-section label,
.comment-visibility-section label {
  font-size: 1rem;
  margin-right: 16px;
}
    [data-hidden="false"] {
      background-color:white !important;
      text-decoration: none !important;
      cursor: none !important;
    }
    [data-hidden="true"] {
       display:none;
}

      
  `;
  document.head.appendChild(style);
}

export default TrackChanges;
